import { useEffect, useMemo, useState, Fragment } from 'react'
import { useDispatch } from 'react-redux'
import {
  format,
  eachMonthOfInterval,
  min,
  max,
  addDays,
  endOfMonth,
} from 'date-fns'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { Switch } from '@headlessui/react'
import { Link } from 'react-router-dom'

import { CalendarEvent, RepeatingEvent } from '../../store/slices/slices.types'
import { setCalendarDateRange } from '../../store/slices/workspaceSlice'
import { toDateString } from '../../lib/shared/calendar.utils'
import { ListCalendarEvent } from './ListCalendarEvent'
import {
  getAccountingYearLabel,
  isAccountingYearEndMonth,
} from '../../lib/utils/date.utils'

interface ListCalendarProps {
  workspaces: Array<{
    id: string
    name: string
    accountingReferenceDate?: string
  }>
  personalCalendarName?: string
  events: CalendarEvent[]
  repeatingEvents?: RepeatingEvent[]
  showTaxYearEnd?: boolean
  showWorkspaceLabel?: boolean
  showControls?: boolean
  showWorkspaceFilters?: boolean
}

export function ListCalendar({
  workspaces,
  events = [],
  repeatingEvents = [],
  showTaxYearEnd,
  showWorkspaceLabel = true,
  showControls = true,
  showWorkspaceFilters = true,
}: ListCalendarProps) {
  const dispatch = useDispatch()
  const [showEmptyMonths, setShowEmptyMonths] = useState(true)
  const [showCompletedEvents, setShowCompletedEvents] = useState(false)
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Set<string>>(
    new Set(workspaces.map((w) => w.id)),
  )

  const { eventsByMonth, allMonths, monthDates } = useMemo(() => {
    const today = new Date()

    const dates = events.map((event) => new Date(event.date))
    const minDate = min([today, ...(dates.length ? dates : [today])])
    const maxDate = dates.length ? max(dates) : today

    const monthsInRange = eachMonthOfInterval({ start: minDate, end: maxDate })
    const allMonths = monthsInRange.map((date) => format(date, 'MMMM yyyy'))

    const monthDates = monthsInRange.reduce(
      (acc, date) => {
        acc[format(date, 'MMMM yyyy')] = date
        return acc
      },
      {} as Record<string, Date>,
    )

    const eventsByMonth = events.reduce(
      (acc, event) => {
        const eventDate = new Date(event.date)
        const yearMonth = format(eventDate, 'MMMM yyyy')
        if (!acc[yearMonth]) {
          acc[yearMonth] = []
        }
        acc[yearMonth].push({
          ...event,
          repeatingEvent:
            repeatingEvents.find(
              (repeatingEvent) => repeatingEvent.id === event.repeatingEventId,
            ) ?? null,
        })
        return acc
      },
      {} as Record<
        string,
        Array<CalendarEvent & { repeatingEvent: RepeatingEvent | null }>
      >,
    )

    return { eventsByMonth, allMonths, monthDates }
  }, [events, repeatingEvents])

  useEffect(() => {
    dispatch(
      setCalendarDateRange({
        startDate: toDateString(new Date()),
        endDate: toDateString(addDays(new Date(), 365)),
      }),
    )
  }, [dispatch])

  const displayMonths = useMemo(() => {
    if (showEmptyMonths) return allMonths
    // Only filter out months that have no events at all, regardless of completion status
    return allMonths.filter((month) => eventsByMonth[month]?.length > 0)
  }, [allMonths, eventsByMonth, showEmptyMonths])

  const filteredAndSortedEvents = useMemo(() => {
    return (
      monthEvents: (typeof eventsByMonth)[keyof typeof eventsByMonth] = [],
    ) =>
      (monthEvents || [])
        .filter(
          (event) =>
            (showCompletedEvents || !event.isCompleted) &&
            selectedWorkspaces.has(event.workspaceId ?? ''),
        )
        .sort((a, b) => a.date.localeCompare(b.date))
  }, [showCompletedEvents, selectedWorkspaces])

  console.log({ workspaces })

  return (
    <div className="space-y-8 pb-16">
      {showControls && (
        <div className="border rounded-lg border-gray-200 px-4 py-3 bg-gray-100 flex flex-col gap-4">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">
              Display Options
            </h4>
            <div className="flex items-center gap-3">
              <Switch
                id="empty-months-toggle"
                checked={showEmptyMonths}
                onChange={setShowEmptyMonths}
                className={`${
                  showEmptyMonths ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span className="sr-only">Show months with no events</span>
                <span
                  className={`${
                    showEmptyMonths ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <label
                htmlFor="empty-months-toggle"
                className="text-sm font-medium text-gray-700"
              >
                Show empty months
              </label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="completed-events-toggle"
                checked={showCompletedEvents}
                onChange={setShowCompletedEvents}
                className={`${
                  showCompletedEvents ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span className="sr-only">Show completed events</span>
                <span
                  className={`${
                    showCompletedEvents ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <label
                htmlFor="completed-events-toggle"
                className="text-sm font-medium text-gray-700"
              >
                Show completed events
              </label>
            </div>
          </div>

          {showWorkspaceFilters && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">
                Calendar Filters
              </h4>
              <div className="flex flex-col gap-2">
                {workspaces.map((space) => (
                  <label key={space.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedWorkspaces.has(space.id)}
                      onChange={(e) => {
                        setSelectedWorkspaces((prev) => {
                          const next = new Set(prev)
                          if (e.target.checked) {
                            next.add(space.id)
                          } else {
                            next.delete(space.id)
                          }
                          return next
                        })
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {space.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {(displayMonths.length > 0
          ? displayMonths
          : [format(new Date(), 'MMMM yyyy')]
        ).map((yearMonth) => {
          const monthDate = monthDates[yearMonth]
          const workspacesWithYearEnd = workspaces.filter((space) => {
            if (!space.accountingReferenceDate) return false
            if (!selectedWorkspaces.has(space.id)) return false
            if (
              isAccountingYearEndMonth(monthDate, space.accountingReferenceDate)
            ) {
              return true
            }
            return false
          })
          return (
            <Fragment key={yearMonth}>
              <div className="month-group bg-white rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold px-6 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                  {yearMonth}
                </h3>
                <ul className="divide-y divide-gray-100">
                  {filteredAndSortedEvents(eventsByMonth[yearMonth]).map(
                    (event) => {
                      const repeatingEvent = repeatingEvents.find(
                        (re) => re.id === event.repeatingEventId,
                      )
                      const workspace = workspaces.find(
                        (w) => w.id === event.workspaceId,
                      )
                      return (
                        <div
                          key={event.workspaceId + event.date + event.name}
                          className="group relative hover:bg-gray-50 transition-colors"
                        >
                          <Link
                            to={`/workspace/${event.workspaceId}/calendar/event/${event.id}`}
                            className="absolute inset-0 z-10"
                            aria-label={`View details for ${event.name}`}
                          />
                          <ListCalendarEvent
                            event={event}
                            repeatingEvent={repeatingEvent ?? null}
                            calendarName={workspace?.name}
                          />
                        </div>
                      )
                    },
                  )}
                  {(!eventsByMonth[yearMonth] ||
                    eventsByMonth[yearMonth].length === 0) && (
                    <li className="px-6 py-4 text-sm text-gray-500 italic">
                      No events this month
                    </li>
                  )}
                  {!showCompletedEvents &&
                    eventsByMonth[yearMonth]?.some((e) => e.isCompleted) && (
                      <li className="px-6 py-4 text-sm text-gray-500 italic border-t border-gray-100">
                        {
                          eventsByMonth[yearMonth].filter(
                            (e) =>
                              e.isCompleted &&
                              selectedWorkspaces.has(e.workspaceId ?? ''),
                          ).length
                        }{' '}
                        completed{' '}
                        {eventsByMonth[yearMonth].filter(
                          (e) =>
                            e.isCompleted &&
                            selectedWorkspaces.has(e.workspaceId ?? ''),
                        ).length === 1
                          ? 'event'
                          : 'events'}{' '}
                        hidden
                      </li>
                    )}
                </ul>
              </div>

              {workspacesWithYearEnd.map((w) => {
                if (!w.accountingReferenceDate) {
                  return null
                }
                return (
                  <div className="relative" key={w.id}>
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="w-full border-t border-blue-300" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-sm text-blue-600 font-medium">
                        {getAccountingYearLabel(
                          monthDates[yearMonth],
                          new Date(w.accountingReferenceDate),
                          showWorkspaceLabel ? w.name : undefined,
                        )}
                      </span>
                    </div>
                  </div>
                )
              })}

              {showTaxYearEnd &&
                endOfMonth(monthDates[yearMonth]).getMonth() === 2 && (
                  <div className="relative">
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="w-full border-t border-blue-300" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-sm text-purple-500 font-medium">
                        End of UK tax year - 5th April
                      </span>
                    </div>
                  </div>
                )}
            </Fragment>
          )
        })}

        {events.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No events
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new event.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
